/* 
    Linguistic-aware line break engine for subtitle text.
    Scores every possible word-boundary split position using subtitle best-practice weights,
    then picks the highest-scoring split that keeps lines under maxChars.
    Visual balance (line-length difference) is used as a secondary tiebreaker.
*/

// --- English word classification lists ---

const conjunctions = new Set(["and", "but", "or", "nor", "so", "yet", "for", "because", "since", "although", "though", "while", "when", "whenever", "if", "unless", "until", "that", "which", "who", "whom", "whose", "where", "whereas", "whether", "however", "therefore", "moreover", "furthermore", "nevertheless", "nonetheless", "otherwise", "then", "than", "as"]);
const prepositions = new Set(["in", "on", "at", "to", "for", "with", "from", "by", "of", "about", "into", "through", "during", "before", "after", "above", "below", "between", "under", "over", "without", "within", "along", "across", "behind", "beyond", "toward", "towards", "upon", "against", "among", "around", "beside", "despite", "except", "near", "off", "outside", "inside", "past", "throughout", "underneath"]);
const articles = new Set(["a", "an", "the", "this", "that", "these", "those", "my", "your", "his", "her", "its", "our", "their", "some", "any", "no", "every", "each", "all", "both", "few", "several", "many", "much", "most"]);
const subjectPronouns = new Set(["i", "you", "he", "she", "it", "we", "they"]);
const auxiliaries = new Set(["is", "are", "was", "were", "am", "be", "been", "being", "has", "have", "had", "having", "do", "does", "did", "will", "would", "shall", "should", "can", "could", "may", "might", "must"]);
const midPunctuation = new Set([",", ";", ":", "—", "–"]);
const endPunctuation = new Set([".", "?", "!", "…"]);

// --- Scoring function ---

/**
 * Score a potential break point between words[splitIndex-1] and words[splitIndex].
 * Higher score = more desirable break point.
 * @param {string[]} words - Array of plain-text words
 * @param {number} splitIndex - Index of the first word on the new line (break happens before this word)
 * @returns {number} linguistic quality score
 */
function scoreBreakPoint(words, splitIndex) {
  let score = 0;
  let wordBefore = words[splitIndex - 1]; // last word on current line
  let wordAfter = words[splitIndex]; // first word on next line
  let wordBeforeLower = wordBefore.toLowerCase();
  let wordAfterLower = wordAfter.toLowerCase();

  // Strip trailing punctuation for classification of the word itself
  let wordBeforeBase = wordBeforeLower.replace(/[.,;:!?…—–"')}\]]+$/, "");
  let lastCharBefore = wordBefore.slice(-1);

  // --- Positive signals (prefer breaking here) ---

  // Break after sentence-ending punctuation
  if (endPunctuation.has(lastCharBefore) || wordBefore.endsWith("...")) {
    score += 100;
  }

  // Break after mid-sentence punctuation (comma, semicolon, colon, dash)
  if (midPunctuation.has(lastCharBefore)) {
    score += 80;
  }

  // Break before conjunction
  if (conjunctions.has(wordAfterLower)) {
    score += 60;
  }

  // Break before preposition
  if (prepositions.has(wordAfterLower)) {
    score += 40;
  }

  // --- Negative signals (avoid breaking here) ---

  // Don't separate article/determiner from its noun
  if (articles.has(wordBeforeBase)) {
    score -= 80;
  }

  // Don't separate subject pronoun from verb
  if (subjectPronouns.has(wordBeforeBase)) {
    score -= 60;
  }

  // Don't separate auxiliary/modal from what follows
  if (auxiliaries.has(wordBeforeBase)) {
    score -= 60;
  }

  // Don't separate negation from what follows
  if (wordBeforeBase === "not" || wordBefore.endsWith("n't")) {
    score -= 80;
  }

  // Don't separate "to" when it's likely an infinitive marker (to + verb)
  // This overlaps with preposition, but the negative should dominate for "to run", "to be", etc.
  if (wordBeforeBase === "to") {
    score -= 40;
  }
  return score;
}

/**
 * Post-process helper: re-split any lines that exceed maxChars
 * by greedily breaking them into shorter lines.
 */
function splitOverflowLines(lines, maxChars, getWordLength) {
  let result = [];
  for (let line of lines) {
    if (getWordLength(line) <= maxChars) {
      result.push(line);
    } else {
      // Split overflowing line into sub-lines
      let words = line.split(" ");
      let currentLine = "";
      for (let word of words) {
        let testLine = currentLine ? currentLine + " " + word : word;
        if (getWordLength(testLine) <= maxChars) {
          currentLine = testLine;
        } else {
          if (currentLine) result.push(currentLine);
          currentLine = word;
        }
      }
      if (currentLine) result.push(currentLine);
    }
  }
  return result;
}

/**
 * Find the best line-break positions for an array of words.
 * Returns an array of strings (lines), each under maxChars.
 * 
 * @param {string[]} words - Array of plain-text words
 * @param {number} maxChars - Maximum characters per line
 * @param {number} maxLines - Maximum number of lines
 * @param {function} [getWordLength] - Optional function to get display length of a word. Defaults to word.length.
 * @returns {string[]} Array of line strings
 */
function findBestBreakPoints(words, maxChars, maxLines, getWordLength) {
  if (!getWordLength) {
    getWordLength = function (w) {
      return w.length;
    };
  }

  // Build a full text to check if it fits on one line
  let fullText = words.join(" ");
  if (getWordLength(fullText) <= maxChars) {
    return [fullText];
  }
  if (maxLines >= 2) {
    // For 2-line splits: exhaustive search (O(N), no recursion)
    // For 3+ line splits: greedy — pick best first-line break, then recurse (O(N × L))
    let bestResult = null;
    let bestScore = -Infinity;
    for (let i = 1; i < words.length; i++) {
      let line1 = words.slice(0, i).join(" ");
      let len1 = getWordLength(line1);

      // Skip if first line is too long
      if (len1 > maxChars) continue;
      let remainingWords = words.slice(i);
      let remainingText = remainingWords.join(" ");
      let lenRemaining = getWordLength(remainingText);
      let linguisticScore = scoreBreakPoint(words, i);
      let result;
      if (lenRemaining <= maxChars) {
        // Remainder fits on one line — 2-line result
        result = [line1, remainingText];
        let balanceDiff = Math.abs(len1 - lenRemaining);
        let maxLen = Math.max(len1, lenRemaining);
        linguisticScore += maxLen > 0 ? 20 * (1 - balanceDiff / maxLen) : 0;
      } else if (maxLines > 2) {
        // For 3+ lines, use greedy: score first-line break,
        // pick best, then recurse on remainder (O(N × L) total).
        // Use proximity factor to dampen linguistic preferences
        // when line length is far from target (prevents very short lines).
        let targetLen = getWordLength(fullText) / maxLines;
        let deviation = Math.abs(len1 - targetLen);
        let proximityFactor = targetLen > 0 ? Math.max(0.1, 1 - deviation / targetLen) : 1;
        let targetScore = targetLen > 0 ? 80 * proximityFactor : 0;
        linguisticScore = linguisticScore * proximityFactor + targetScore;

        // Penalize if remaining text won't fit evenly in remaining lines
        let avgRemaining = lenRemaining / Math.max(1, maxLines - 1);
        if (avgRemaining > maxChars) {
          linguisticScore -= 50 * (avgRemaining / maxChars - 1);
        }
        result = [line1]; // placeholder, will recurse after picking best
      } else {
        // Only 2 lines allowed and remainder doesn't fit — skip
        continue;
      }
      if (linguisticScore > bestScore) {
        bestScore = linguisticScore;
        bestResult = result;
        // Store split index for the greedy recursion path
        bestResult._splitIndex = i;
      }
    }
    if (bestResult) {
      let result;
      if (bestResult.length === 1 && bestResult._splitIndex) {
        // Greedy path: we picked the best first line, now recurse on remainder
        let splitIdx = bestResult._splitIndex;
        let line1 = bestResult[0];
        let remainingWords = words.slice(splitIdx);
        let remainingLines = findBestBreakPoints(remainingWords, maxChars, maxLines - 1, getWordLength);
        result = [line1].concat(remainingLines);
      } else {
        result = bestResult;
      }
      // Post-process: re-split any lines that still exceed maxChars
      return splitOverflowLines(result, maxChars, getWordLength);
    }
  }

  // Fallback: greedy split if no valid scored split found
  return greedySplit(words, maxChars, maxLines, getWordLength);
}

/**
 * Greedy fallback: fill lines left-to-right up to maxChars.
 *
 * This may return more than maxLines. Event-level auto-formatting groups
 * physical lines into multiple subtitle events, but no physical line should
 * exceed maxChars when it can be wrapped at word boundaries.
 */
function greedySplit(words, maxChars, maxLines, getWordLength) {
  let lines = [];
  let currentLine = "";
  for (let i = 0; i < words.length; i++) {
    let word = words[i];
    let testLine = currentLine ? currentLine + " " + word : word;
    if (getWordLength(testLine) <= maxChars) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
    }
  }
  if (currentLine) {
    lines.push(currentLine);
  }
  return lines;
}
export default {
  findBestBreakPoints,
  scoreBreakPoint,
  conjunctions,
  prepositions,
  articles,
  subjectPronouns,
  auxiliaries
};